import { ComponentRef, Directive, ElementRef, OnDestroy, Renderer2, ViewContainerRef, effect, inject, input } from '@angular/core';
import { LoaderComponent, LoaderThemeColor } from '@progress/kendo-angular-indicators';

@Directive({
  selector: '[buttonLoading]',
})
export class ButtonLoadingDirective implements OnDestroy {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private vcr = inject(ViewContainerRef);

  readonly isLoading = input<boolean>(false, { alias: "buttonLoading" });
  readonly themeColor = input<LoaderThemeColor>('base', { alias: "buttonLoadingThemeColor" });

  private loaderRef: ComponentRef<LoaderComponent> | null = null;
  private observer: MutationObserver | null = null;

  constructor() {
    this.observer = new MutationObserver((mutations) => {
      if (!this.isLoading()) return;
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if ((node as Element).tagName?.toLowerCase() === 'kendo-icon-wrapper') {
            this.renderer.setStyle(node, 'display', 'none');
          }
        }
      }
    });
    this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });

    effect(() => {
      const isLoading = this.isLoading();
      if (isLoading) {
        this.addSpinner();
      } else {
        this.removeSpinner();
      }
    });
  }

  private addSpinner(): void {
    if (!this.loaderRef) {
      this.loaderRef = this.vcr.createComponent(LoaderComponent);
      this.loaderRef.instance.type = 'converging-spinner';
      this.loaderRef.instance.size = 'small';
      this.loaderRef.instance.themeColor = this.themeColor();
      this.loaderRef.changeDetectorRef.detectChanges();
      this.renderer.insertBefore(this.el.nativeElement, this.loaderRef.location.nativeElement, this.el.nativeElement.firstChild);
    }
    const iconWrapper = this.getKendoIconWrapper();
    if (iconWrapper) {
      this.renderer.setStyle(iconWrapper, 'display', 'none');
    }
  }

  private removeSpinner(): void {
    if (this.loaderRef) {
      this.renderer.removeChild(this.el.nativeElement, this.loaderRef.location.nativeElement);
      this.loaderRef.destroy();
      this.loaderRef = null;
    }
    const iconWrapper = this.getKendoIconWrapper();
    if (iconWrapper) {
      this.renderer.removeStyle(iconWrapper, 'display');
    }
  }

  private getKendoIconWrapper(): Element | null {
    return this.el.nativeElement.querySelector('kendo-icon-wrapper');
  }

  ngOnDestroy(): void {
    this.removeSpinner();
    this.observer?.disconnect();
    this.observer = null;
  }
}
